import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { clientContext } from './client-context';

/**
 * Records a page view on every navigation (`POST /v1/events`), for the admin
 * Traffic page and the dashboard's traffic panel.
 *
 * The browser sends only what it alone knows — path, referrer, title,
 * language, screen size, time zone. The API adds the timestamp, IP address,
 * user agent (parsed into browser / OS / device) and Accept-Language from the
 * request itself, so those cannot be faked from here. See "The events table"
 * in `../api-repo/CLAUDE.md`.
 *
 * Deliberately not sent:
 * - when the browser asks not to be tracked (Do-Not-Track or Global Privacy
 *   Control) — IP addresses and user agents are personal data;
 * - from the admin area, so admins reading the numbers don't inflate them;
 * - from automated browsers (`navigator.webdriver`), e.g. the build's
 *   Puppeteer prerender, which would otherwise log every route on each build;
 * - from development builds (`ng serve`), which share the production API;
 * - from the Electron desktop app (`environment.desktop`) — it is not website
 *   traffic;
 * - when no API is configured.
 *
 * `fetch` with `keepalive` rather than `HttpClient`: the request survives the
 * tab closing, and it skips the auth interceptor — the endpoint is public and
 * a page view has no reason to carry the admin's token.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  /**
   * Production builds only: the dev build talks to the same API, and a
   * developer's local browsing would otherwise count as real traffic.
   */
  private readonly endpoint =
    environment.production && !environment.desktop && environment.apiUrl
      ? `${environment.apiUrl.replace(/\/$/, '')}/v1/events`
      : '';

  /** The previous in-app URL: the referrer of the next view. */
  private previous: string | null = null;
  private started = false;

  /** Start recording. Called once, from `AppComponent.ngOnInit`. */
  init(): void {
    if (this.started || !this.endpoint || !this.allowed()) {
      return;
    }
    this.started = true;
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(e => this.record(e.urlAfterRedirects));
  }

  private record(url: string): void {
    const path = url || '/';
    // The first view's referrer is where the visitor came from; after that it
    // is the page they navigated from inside the app.
    const referrer =
      this.previous === null
        ? this.doc.referrer
        : `${this.doc.location.origin}${this.previous}`;
    this.previous = path;

    if (/^\/admin(\/|$)/.test(path)) {
      return;
    }

    const body = {
      path: path.slice(0, 2048),
      // The title is set by SeoService on the same navigation; read it after
      // this tick so it is the new page's, not the previous one's.
      title: '',
      ...clientContext(this.doc, referrer),
    };
    setTimeout(() => {
      body.title = this.doc.title.slice(0, 300);
      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
        // No cookies or auth: a page view is anonymous on the way in.
        credentials: 'omit',
      }).catch(() => {
        // Analytics must never break or slow the site; a lost view is fine.
      });
    }, 0);
  }

  /** Honour the browser's privacy signals and skip automation. */
  private allowed(): boolean {
    const nav = this.doc.defaultView?.navigator as
      | (Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string })
      | undefined;
    if (!nav || nav.webdriver) {
      return false;
    }
    const dnt = nav.doNotTrack ?? nav.msDoNotTrack;
    return dnt !== '1' && dnt !== 'yes' && nav.globalPrivacyControl !== true;
  }
}
